import { useState, useCallback } from "react";
import { Check, ChevronDown, Palette } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { setLocalStorage, LOCAL_STORAGE_KEYS } from "../../lib/utils/localStorageUtil";
import COLOR_SCHEMES, { getSavedColorScheme, applyColorScheme } from "../../lib/constants/colorSchemes";
import "./Settings.scss";

const bodyVariants = {
  collapsed: { height: 0, opacity: 0, overflow: "hidden" },
  expanded: { height: "auto", opacity: 1, overflow: "visible", transition: { duration: 0.25, ease: "easeInOut" } },
};

export default function Settings() {
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  // Color Scheme state
  const [savedColorScheme, setSavedColorScheme] = useState(getSavedColorScheme);
  const [draftColorScheme, setDraftColorScheme] = useState(savedColorScheme);
  const isColorDirty = draftColorScheme !== savedColorScheme;

  const handleColorSave = useCallback(() => {
    setSavedColorScheme(draftColorScheme);
    setLocalStorage(LOCAL_STORAGE_KEYS.COLOR_SCHEME, draftColorScheme);
    applyColorScheme(draftColorScheme);
  }, [draftColorScheme]);

  const handleColorCancel = useCallback(() => {
    setDraftColorScheme(savedColorScheme);
  }, [savedColorScheme]);

  return (
    <div className="settings-page">
      <div className="settings-page__header">
        <h1 className="settings-page__title">Settings</h1>
        <p className="settings-page__subtitle">Application Preferences</p>
      </div>

      <div className="settings-page__content">
        {/* Color Scheme Setting Card */}
        <div className="settings-card">
          <button
            type="button"
            className="settings-card__header settings-card__header--toggle"
            onClick={() => setIsColorExpanded(prev => !prev)}
          >
            <div className="settings-card__icon">
              <Palette size={20} />
            </div>
            <div className="settings-card__header-text">
              <h2 className="settings-card__title">Color Scheme</h2>
              <p className="settings-card__description">
                Choose the accent color used across buttons, links, and interactive elements.
              </p>
            </div>
            <ChevronDown
              size={18}
              className={`settings-card__chevron ${isColorExpanded ? "settings-card__chevron--open" : ""}`}
            />
          </button>

          <AnimatePresence initial={false}>
            {isColorExpanded && (
              <motion.div
                className="settings-card__body"
                variants={bodyVariants}
                initial="collapsed"
                animate="expanded"
                exit="collapsed"
              >
                <div className="color-scheme-grid">
                  {COLOR_SCHEMES.map(scheme => (
                    <button
                      key={scheme.value}
                      type="button"
                      className={`color-scheme-option ${draftColorScheme === scheme.value ? "color-scheme-option--selected" : ""}`}
                      onClick={() => setDraftColorScheme(scheme.value)}
                    >
                      <span className="color-scheme-option__swatch" style={{ background: scheme.color }} />
                      <span className="color-scheme-option__label">
                        {scheme.label}
                        {scheme.isDefault && <span className="color-scheme-option__default-badge">Default</span>}
                      </span>
                      {draftColorScheme === scheme.value && <Check size={14} className="color-scheme-option__check" />}
                    </button>
                  ))}
                </div>

                {/* Save / Cancel */}
                <div className={`settings-card__actions ${isColorDirty ? "settings-card__actions--visible" : ""}`}>
                  <button
                    type="button"
                    className="settings-card__btn settings-card__btn--cancel"
                    onClick={handleColorCancel}
                    disabled={!isColorDirty}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="settings-card__btn settings-card__btn--save"
                    onClick={handleColorSave}
                    disabled={!isColorDirty}
                  >
                    Save
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
